import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Container, EmptyContent, Pagination, Text } from "rothzerg-ui";
import { BackToTopBar } from "@/components/catalog/BackToTopBar";
import { BreadcrumbNav } from "@/components/catalog/BreadcrumbNav";
import { CategoryToolbar } from "@/components/catalog/CategoryToolbar";
import { FeedbackBanner } from "@/components/catalog/FeedbackBanner";
import { FilterSheet } from "@/components/catalog/FilterSheet";
import { ProductGrid } from "@/components/catalog/ProductGrid";
import { RelatedSearchTags } from "@/components/catalog/RelatedSearchTags";
import { getRelatedSearchTerms, searchSuggestions } from "@/data/search";
import {
  paginateProducts,
  searchProducts,
  sortProducts,
} from "@/data/selectors";
import { homePath } from "@/lib/routes";
import type { SortOption } from "@/types/catalog";
import { Link } from "react-router-dom";
import { searchPath } from "@/lib/routes";

const PAGE_SIZE = 12;

export function SearchPage() {
  const [searchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [sort, setSort] = useState<SortOption>("relevance");
  const [includeMarketplace, setIncludeMarketplace] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);
  const [page, setPage] = useState(1);

  const allProducts = useMemo(() => {
    let items = searchProducts(query);
    if (!includeMarketplace) {
      items = items.filter((p) => p.seller === "kmart");
    }
    return sortProducts(items, sort);
  }, [query, includeMarketplace, sort]);

  const { items, rangeStart, rangeEnd, totalCount, totalPages } = paginateProducts(
    allProducts,
    page,
    PAGE_SIZE,
  );

  const relatedTerms = getRelatedSearchTerms(query);

  if (!query) {
    return (
      <Container>
        <Text variant="h2">Search</Text>
        <Text variant="body">Try searching for sweat, optus, or uno</Text>
        <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem", flexWrap: "wrap" }}>
          {searchSuggestions.map((term) => (
            <Link key={term} to={searchPath(term)}>
              {term}
            </Link>
          ))}
        </div>
      </Container>
    );
  }

  return (
    <div id="search-page" className="kmart-page">
      <Container>
        <BreadcrumbNav
          items={[
            { label: "Home", href: homePath() },
            { label: `Search: ${query}` },
          ]}
        />

        <Text id="search-results-title" variant="h2" className="kmart-search-title">
          Showing results for: <strong>{query}</strong>
        </Text>

        <RelatedSearchTags terms={relatedTerms} />

        {totalCount === 0 ? (
          <div id="search-no-results">
            <EmptyContent
              title="No results found"
              description={`We couldn't find products matching "${query}". Try another search.`}
              actions={
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                  {searchSuggestions.map((term) => (
                    <Link key={term} to={searchPath(term)}>
                      {term}
                    </Link>
                  ))}
                </div>
              }
            />
          </div>
        ) : (
          <>
            <CategoryToolbar
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              totalCount={totalCount}
              sort={sort}
              onSortChange={setSort}
              includeMarketplace={includeMarketplace}
              onMarketplaceChange={setIncludeMarketplace}
              onFilterOpen={() => setFilterOpen(true)}
            />

            <ProductGrid products={items} showSwatches />

            {totalPages > 1 && (
              <div style={{ marginTop: "2rem" }}>
                <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
              </div>
            )}
          </>
        )}
      </Container>

      <div className="kmart-value-props">
        <Container>
          <div>
            <strong>Low prices</strong>
            <p>Everyday value on thousands of products</p>
          </div>
          <div>
            <strong>Convenient</strong>
            <p>Click & Collect and home delivery</p>
          </div>
          <div>
            <strong>Safe & secure</strong>
            <p>Shop with confidence online</p>
          </div>
        </Container>
      </div>

      <BackToTopBar />
      <FeedbackBanner />
      <FilterSheet open={filterOpen} onOpenChange={setFilterOpen} />
    </div>
  );
}
